const SUPPORTED_EQUATIONS = {
  linear: ['m', 'b'],
  verticalLine: ['c'],
  cubicStandard: ['a', 'b', 'c', 'd'],
  quadraticStandard: ['a', 'b', 'c'],
  quadraticVertex: ['a', 'p', 'q'],
  hyperbola: ['k', 'p', 'q'],
  exponential: ['a', 'b', 'p', 'q'],
  sine: ['A', 'k', 'p', 'q'],
  cosine: ['A', 'k', 'p', 'q'],
  tangent: ['A', 'k', 'p', 'q'],
};

const SUPPORTED_GRAPH_KINDS = new Set(['function', 'table', 'transform', 'derivative', 'inverse']);
const SUPPORTED_LINE_STYLES = new Set(['solid', 'dashed', 'dotted']);
const ARRAY_STATE_FIELDS = [
  'annotationStrokes',
  'annotationFuture',
  'handwritingStrokes',
  'handwritingFuture',
  'tableRows',
];

const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
const isFiniteNumberLike = (value) =>
  value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value));

const unwrapJsonFence = (input) => {
  const trimmed = String(input || '').trim();
  const fenced = trimmed.match(/^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```$/i);
  return fenced ? fenced[1].trim() : trimmed;
};

const addError = (errors, path, message) => {
  errors.push({ path, message });
};

const validateViewport = (viewport, path, errors) => {
  if (viewport === undefined) return;
  if (!isRecord(viewport)) {
    addError(errors, path, 'must be an object');
    return;
  }

  ['xMin', 'xMax', 'yMin', 'yMax'].forEach((field) => {
    if (!isFiniteNumberLike(viewport[field])) {
      addError(errors, `${path}.${field}`, 'must be a finite number');
    }
  });

  if (
    isFiniteNumberLike(viewport.xMin) &&
    isFiniteNumberLike(viewport.xMax) &&
    Number(viewport.xMin) >= Number(viewport.xMax)
  ) {
    addError(errors, path, 'xMin must be less than xMax');
  }
  if (
    isFiniteNumberLike(viewport.yMin) &&
    isFiniteNumberLike(viewport.yMax) &&
    Number(viewport.yMin) >= Number(viewport.yMax)
  ) {
    addError(errors, path, 'yMin must be less than yMax');
  }
};

const validateFunctionGraph = (graph, path, errors) => {
  const requiredParams = SUPPORTED_EQUATIONS[graph.equationType];
  if (!requiredParams) {
    addError(errors, `${path}.equationType`, 'is not a supported equation type');
    return;
  }
  if (!isRecord(graph.params)) {
    addError(errors, `${path}.params`, 'must be an object');
    return;
  }
  requiredParams.forEach((param) => {
    if (!isFiniteNumberLike(graph.params[param])) {
      addError(errors, `${path}.params.${param}`, 'must be a finite number');
    }
  });
};

const validateTableGraph = (graph, path, errors) => {
  if (!Array.isArray(graph.points) || graph.points.length < 2) {
    addError(errors, `${path}.points`, 'must contain at least two points');
    return;
  }
  graph.points.forEach((point, pointIndex) => {
    const pointPath = `${path}.points[${pointIndex}]`;
    if (!isRecord(point)) {
      addError(errors, pointPath, 'must be an object');
      return;
    }
    if (!isFiniteNumberLike(point.x)) addError(errors, `${pointPath}.x`, 'must be a finite number');
    if (!isFiniteNumberLike(point.y)) addError(errors, `${pointPath}.y`, 'must be a finite number');
  });
};

const validateGraph = (graph, path, errors) => {
  if (!isRecord(graph)) {
    addError(errors, path, 'must be an object');
    return;
  }
  if (!isNonEmptyString(graph.id)) addError(errors, `${path}.id`, 'must be a non-empty string');
  if (!SUPPORTED_GRAPH_KINDS.has(graph.kind)) {
    addError(errors, `${path}.kind`, 'is not a supported graph kind');
    return;
  }
  if (graph.lineStyle !== undefined && !SUPPORTED_LINE_STYLES.has(graph.lineStyle)) {
    addError(errors, `${path}.lineStyle`, 'must be solid, dashed, or dotted');
  }
  if (graph.visible !== undefined && typeof graph.visible !== 'boolean') {
    addError(errors, `${path}.visible`, 'must be a boolean');
  }
  if (graph.kind === 'function') validateFunctionGraph(graph, path, errors);
  if (graph.kind === 'table') validateTableGraph(graph, path, errors);
  if (['transform', 'derivative', 'inverse'].includes(graph.kind) && !isNonEmptyString(graph.sourceGraphId)) {
    addError(errors, `${path}.sourceGraphId`, 'must reference a graph on the same slide');
  }
  if (graph.kind === 'transform') {
    ['horizontalShift', 'verticalShift'].forEach((field) => {
      if (graph[field] !== undefined && !isFiniteNumberLike(graph[field])) {
        addError(errors, `${path}.${field}`, 'must be a finite number');
      }
    });
    ['reflectX', 'reflectY'].forEach((field) => {
      if (graph[field] !== undefined && typeof graph[field] !== 'boolean') {
        addError(errors, `${path}.${field}`, 'must be a boolean');
      }
    });
  }
};

const validateCanvasState = (state, path, errors, allGraphIds) => {
  if (!isRecord(state)) {
    addError(errors, path, 'must be an object');
    return;
  }
  if (state.graphs !== undefined && !Array.isArray(state.graphs)) {
    addError(errors, `${path}.graphs`, 'must be an array');
    return;
  }

  const graphs = Array.isArray(state.graphs) ? state.graphs : [];
  const slideGraphIds = new Set();
  graphs.forEach((graph, graphIndex) => {
    const graphPath = `${path}.graphs[${graphIndex}]`;
    validateGraph(graph, graphPath, errors);
    if (!isNonEmptyString(graph?.id)) return;
    if (allGraphIds.has(graph.id)) {
      addError(errors, `${graphPath}.id`, 'must be unique across the document');
      return;
    }
    allGraphIds.add(graph.id);
    slideGraphIds.add(graph.id);
  });

  graphs.forEach((graph, graphIndex) => {
    if (
      ['transform', 'derivative', 'inverse'].includes(graph?.kind) &&
      isNonEmptyString(graph.sourceGraphId) &&
      !slideGraphIds.has(graph.sourceGraphId)
    ) {
      addError(
        errors,
        `${path}.graphs[${graphIndex}].sourceGraphId`,
        'must reference a graph on the same slide'
      );
    }
  });

  ['selectedGraphId', 'compareGraphId'].forEach((field) => {
    if (state[field] && !slideGraphIds.has(state[field])) {
      addError(errors, `${path}.${field}`, 'must reference a graph on the same slide');
    }
  });

  ARRAY_STATE_FIELDS.forEach((field) => {
    if (state[field] !== undefined && !Array.isArray(state[field])) {
      addError(errors, `${path}.${field}`, 'must be an array');
    }
  });
  validateViewport(state.viewport, `${path}.viewport`, errors);
};

export const GRAPH_SKETCH_SCHEMA_EXAMPLE = JSON.stringify({
  contentType: 'graph-sketch',
  toolType: 'graph-sketch-tool',
  title: 'Imported linear graph',
  status: 'draft',
  sketchData: {
    version: 2,
    activeSlideId: 'slide-1',
    slides: [
      {
        id: 'slide-1',
        title: 'Canvas 1',
        state: {
          graphs: [
            {
              id: 'graph-1',
              kind: 'function',
              equationType: 'linear',
              params: { m: 2, b: 1 },
              title: 'y = 2x + 1',
              color: '#2563EB',
              lineStyle: 'solid',
              visible: true,
            },
          ],
          viewport: { xMin: -10, xMax: 10, yMin: -10, yMax: 10 },
          gridMode: 'cartesian',
          showAxisLabels: true,
          showAxisMarkers: true,
        },
      },
    ],
  },
}, null, 2);

export const parseGraphSketchDocumentSchema = (input) => {
  let parsed;
  try {
    parsed = JSON.parse(unwrapJsonFence(input));
  } catch (error) {
    return {
      ok: false,
      errors: [{ path: '$', message: `is not valid JSON: ${error.message}` }],
    };
  }

  const errors = [];
  if (!isRecord(parsed)) {
    return { ok: false, errors: [{ path: '$', message: 'must be a JSON object' }] };
  }
  if (parsed.contentType !== 'graph-sketch') {
    addError(errors, '$.contentType', 'must equal "graph-sketch"');
  }
  if (parsed.toolType !== 'graph-sketch-tool') {
    addError(errors, '$.toolType', 'must equal "graph-sketch-tool"');
  }
  if (!isNonEmptyString(parsed.title)) {
    addError(errors, '$.title', 'must be a non-empty string');
  }
  if (!isRecord(parsed.sketchData)) {
    addError(errors, '$.sketchData', 'must be an object');
  } else {
    if (parsed.sketchData.version !== 2) {
      addError(errors, '$.sketchData.version', 'must equal 2');
    }
    if (!Array.isArray(parsed.sketchData.slides) || parsed.sketchData.slides.length === 0) {
      addError(errors, '$.sketchData.slides', 'must contain at least one slide');
    } else {
      const slideIds = new Set();
      const allGraphIds = new Set();
      parsed.sketchData.slides.forEach((slide, slideIndex) => {
        const slidePath = `$.sketchData.slides[${slideIndex}]`;
        if (!isRecord(slide)) {
          addError(errors, slidePath, 'must be an object');
          return;
        }
        if (!isNonEmptyString(slide.id)) {
          addError(errors, `${slidePath}.id`, 'must be a non-empty string');
        } else if (slideIds.has(slide.id)) {
          addError(errors, `${slidePath}.id`, 'must be unique');
        } else {
          slideIds.add(slide.id);
        }
        if (slide.title !== undefined && !isNonEmptyString(slide.title)) {
          addError(errors, `${slidePath}.title`, 'must be a non-empty string when provided');
        }
        validateCanvasState(slide.state, `${slidePath}.state`, errors, allGraphIds);
      });
      if (
        parsed.sketchData.activeSlideId !== undefined &&
        !slideIds.has(parsed.sketchData.activeSlideId)
      ) {
        addError(errors, '$.sketchData.activeSlideId', 'must reference an existing slide');
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      title: parsed.title.trim(),
      sketchData: JSON.parse(JSON.stringify(parsed.sketchData)),
    },
  };
};

